import { useState, useEffect } from 'react';
import API from '../utils/API';
import './createFeedbackRequestModal.css';

const STEPS = ['Client', 'Bills', 'Recipients', 'Configure'];

export default function CreateFeedbackRequestModal({ teamId, clients, isStakeholderGroup, onClose, onCreated }) {
    const [step, setStep] = useState(0);
    const [selectedClient, setSelectedClient] = useState(null);

    const [trackedBills, setTrackedBills] = useState([]);
    const [workspaceBills, setWorkspaceBills] = useState([]);
    const [billSearch, setBillSearch] = useState('');
    const [billSearchResults, setBillSearchResults] = useState([]);
    const [selectedBillIds, setSelectedBillIds] = useState([]);
    const [extraBills, setExtraBills] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const [selectedContactIds, setSelectedContactIds] = useState([]);

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [daysUntilExpiry, setDaysUntilExpiry] = useState(7);
    const [shareWithTeam, setShareWithTeam] = useState(true);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');

    // Load tracked bills when client/group changes
    useEffect(() => {
        if (!selectedClient) return;
        setTrackedBills([]);
        setWorkspaceBills([]);
        setSelectedBillIds([]);
        setExtraBills([]);
        setSelectedContactIds([]);
        API.getClientTrackedBills(teamId, selectedClient.client_id)
            .then(res => setTrackedBills(res.data))
            .catch(err => console.error('Error loading tracked bills:', err));
        if (isStakeholderGroup) {
            API.getWorkspaceTrackedBills(teamId)
                .then(res => setWorkspaceBills(res.data))
                .catch(err => console.error('Error loading workspace bills:', err));
        }
    }, [teamId, selectedClient, isStakeholderGroup]);

    // Bill search debounce
    useEffect(() => {
        if (billSearch.trim().length < 2) {
            setBillSearchResults([]);
            return;
        }
        const timer = setTimeout(() => {
            setSearchLoading(true);
            API.searchBills(billSearch.trim())
                .then(res => {
                    const rows = Array.isArray(res.data) ? res.data : (res.data.rows || []);
                    setBillSearchResults(rows.slice(0, 10));
                })
                .catch(() => setBillSearchResults([]))
                .finally(() => setSearchLoading(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [billSearch]);

    const groupBillIds = new Set(trackedBills.map(b => b.bill_id));
    const allBills = isStakeholderGroup
        ? [
            ...trackedBills,
            ...workspaceBills.filter(b => !groupBillIds.has(b.bill_id)),
            ...extraBills.filter(b => !groupBillIds.has(b.bill_id) && !workspaceBills.find(w => w.bill_id === b.bill_id)),
          ]
        : [
            ...trackedBills,
            ...extraBills.filter(b => !trackedBills.find(t => t.bill_id === b.bill_id)),
          ];

    const toggleBill = (bill_id) => {
        setSelectedBillIds(prev =>
            prev.includes(bill_id) ? prev.filter(id => id !== bill_id) : [...prev, bill_id]
        );
    };

    const addExtraBill = (bill) => {
        if (!extraBills.find(b => b.bill_id === bill.bill_id)) {
            setExtraBills(prev => [...prev, bill]);
        }
        if (!selectedBillIds.includes(bill.bill_id)) {
            setSelectedBillIds(prev => [...prev, bill.bill_id]);
        }
        setBillSearch('');
        setBillSearchResults([]);
    };

    const toggleContact = (contact_id) => {
        setSelectedContactIds(prev =>
            prev.includes(contact_id) ? prev.filter(id => id !== contact_id) : [...prev, contact_id]
        );
    };

    const contacts = selectedClient?.TeamClientContacts || [];
    const contactsWithEmail = contacts.filter(c => c.email);

    const handleSend = async () => {
        setSendError('');
        setSending(true);
        try {
            await API.createFeedbackRequest({
                team_id: teamId,
                client_id: selectedClient.client_id,
                title,
                message: message || undefined,
                days_until_expiry: daysUntilExpiry,
                bill_ids: selectedBillIds,
                contact_ids: selectedContactIds,
                share_with_team: shareWithTeam,
            });
            onCreated?.(selectedClient.client_id);
            onClose();
        } catch (err) {
            setSendError(err.response?.data?.msg || 'Failed to send. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const canAdvanceStep0 = selectedClient !== null;
    const canAdvanceStep1 = selectedBillIds.length > 0;
    const canAdvanceStep2 = selectedContactIds.length > 0;
    const canSend = title.trim().length > 0 && !sending;

    const expiryDate = new Date(Date.now() + daysUntilExpiry * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    const canAdvanceCurrent = step === 0 ? canAdvanceStep0 : step === 1 ? canAdvanceStep1 : canAdvanceStep2;

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable cfm-dialog"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header cfm-header">
                        <div>
                            <h5 className="modal-title cfm-title">New Feedback Request</h5>
                            {selectedClient && (
                                <div className="cfm-subtitle">{selectedClient.client_name}</div>
                            )}
                        </div>
                    </div>

                    {/* Step tabs */}
                    <div className="cfm-steps">
                        {STEPS.map((label, i) => (
                            <button
                                key={label}
                                className={`cfm-step-tab${step === i ? ' cfm-step-tab--active' : ''}${i < step ? ' cfm-step-tab--done' : ''}`}
                                onClick={() => i < step && setStep(i)}
                                disabled={i > step}
                            >
                                <span className="cfm-step-num">{i < step ? '✓' : i + 1}</span>
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="modal-body cfm-body">

                        {/* ── Step 0: Client Selection ────────────────── */}
                        {step === 0 && (
                            <div>
                                <p className="cfm-step-desc">
                                    Select the client this feedback request is for.
                                </p>

                                {clients.length === 0 ? (
                                    <p className="text-muted small fst-italic">No clients found for this workspace.</p>
                                ) : (
                                    <div className="cfm-client-list">
                                        {clients.map(client => {
                                            const isSelected = selectedClient?.client_id === client.client_id;
                                            return (
                                                <div
                                                    key={client.client_id}
                                                    className={`cfm-client-row${isSelected ? ' cfm-client-row--selected' : ''}`}
                                                    onClick={() => setSelectedClient(client)}
                                                >
                                                    <span className="cfm-client-name">{client.client_name}</span>
                                                    {isSelected && <i className="bi bi-check2 cfm-client-check" />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Step 1: Bill Selection ──────────────────── */}
                        {step === 1 && (
                            <div>
                                <p className="cfm-step-desc">
                                    Select bills to include in this feedback request.
                                    {trackedBills.length > 0 && ' Tracked bills are listed below.'}
                                </p>

                                {/* Bill search to add extras */}
                                <div className="cfm-bill-search-wrap mb-3">
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="Search for additional bills (bill number or keyword)…"
                                        value={billSearch}
                                        onChange={e => setBillSearch(e.target.value)}
                                    />
                                    {billSearchResults.length > 0 && (
                                        <div className="cfm-bill-search-results">
                                            {billSearchResults.map(bill => (
                                                <div
                                                    key={bill.bill_id}
                                                    className="cfm-bill-search-item"
                                                    onMouseDown={() => addExtraBill(bill)}
                                                >
                                                    <span className="cfm-bill-search-num">{bill.bill_type} {bill.bill_number}</span>
                                                    <span className="cfm-bill-search-title">{bill.title}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {searchLoading && (
                                        <div className="cfm-bill-search-results">
                                            <div className="cfm-bill-search-item text-muted">Searching…</div>
                                        </div>
                                    )}
                                </div>

                                {allBills.length === 0 && (
                                    <p className="text-muted small fst-italic">
                                        {isStakeholderGroup
                                            ? 'No tracked bills for this group. Use the search above to add bills, or select from the list of bills being tracked for the Workplace.'
                                            : 'No tracked bills for this client. Use the search above to add bills.'
                                        }
                                    </p>
                                )}

                                <div className="cfm-bill-list-wrap">
                                <div className="cfm-bill-list">
                                    {isStakeholderGroup ? (() => {
                                        const groupBills = allBills.filter(b => groupBillIds.has(b.bill_id));
                                        const otherBills = allBills.filter(b => !groupBillIds.has(b.bill_id));
                                        const renderRow = bill => (
                                            <label key={bill.bill_id} className="cfm-bill-row">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cfm-check"
                                                    checked={selectedBillIds.includes(bill.bill_id)}
                                                    onChange={() => toggleBill(bill.bill_id)}
                                                />
                                                <div className="cfm-bill-info">
                                                    <span className="cfm-bill-num">{bill.bill_type} {bill.bill_number}</span>
                                                    <span className="cfm-bill-name">{bill.title}</span>
                                                </div>
                                            </label>
                                        );
                                        return (
                                            <>
                                                {groupBills.length > 0 && (
                                                    <div className="cfm-bill-section">
                                                        <div className="cfm-bill-section-header cfm-bill-section-header--group">
                                                            {selectedClient.client_name} Bills
                                                        </div>
                                                        {groupBills.map(renderRow)}
                                                    </div>
                                                )}
                                                {otherBills.length > 0 && (
                                                    <div className="cfm-bill-section mt-2">
                                                        <div className="cfm-bill-section-header">
                                                            Other Workspace Bills
                                                        </div>
                                                        {otherBills.map(renderRow)}
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })() : allBills.map(bill => (
                                        <label key={bill.bill_id} className="cfm-bill-row">
                                            <input
                                                type="checkbox"
                                                className="form-check-input cfm-check"
                                                checked={selectedBillIds.includes(bill.bill_id)}
                                                onChange={() => toggleBill(bill.bill_id)}
                                            />
                                            <div className="cfm-bill-info">
                                                <span className="cfm-bill-num">{bill.bill_type} {bill.bill_number}</span>
                                                <span className="cfm-bill-name">{bill.title}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                </div>

                                {selectedBillIds.length > 0 && (
                                    <div className="cfm-selection-count">
                                        {selectedBillIds.length} bill{selectedBillIds.length !== 1 ? 's' : ''} selected
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Step 2: Recipients ─────────────────────── */}
                        {step === 2 && (
                            <div>
                                <p className="cfm-step-desc">
                                    Choose which contacts will receive the feedback request email.
                                    Only contacts with an email address can receive the link.
                                </p>

                                {contactsWithEmail.length === 0 && (
                                    <div className="cfm-no-contacts">
                                        <i className="bi bi-envelope-x me-2" />
                                        No contacts have an email address. Add email addresses to contacts in the Clients panel first.
                                    </div>
                                )}

                                <div className="cfm-contact-list">
                                    {contacts.map(contact => {
                                        const hasEmail = !!contact.email;
                                        return (
                                            <label
                                                key={contact.contact_id}
                                                className={`cfm-contact-row${!hasEmail ? ' cfm-contact-row--no-email' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input cfm-check"
                                                    checked={selectedContactIds.includes(contact.contact_id)}
                                                    onChange={() => hasEmail && toggleContact(contact.contact_id)}
                                                    disabled={!hasEmail}
                                                />
                                                <div className="cfm-contact-info">
                                                    <span className="cfm-contact-name">{contact.first_name} {contact.last_name}</span>
                                                    {hasEmail
                                                        ? <span className="cfm-contact-email">{contact.email}</span>
                                                        : <span className="cfm-contact-no-email">No email on file</span>
                                                    }
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>

                                {selectedContactIds.length > 0 && (
                                    <div className="cfm-selection-count">
                                        {selectedContactIds.length} recipient{selectedContactIds.length !== 1 ? 's' : ''} selected
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Step 3: Configure ──────────────────────── */}
                        {step === 3 && (
                            <div>
                                <div className="mb-3">
                                    <label className="cfm-field-label">Request Title <span className="text-danger">*</span></label>
                                    <input
                                        className="form-control form-control-sm"
                                        placeholder="e.g. May Legislative Priority Review"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        maxLength={255}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="cfm-field-label">Personal Message <span className="cfm-optional">(optional)</span></label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        rows={3}
                                        placeholder="Include a note to the recipient(s)…"
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="cfm-field-label">
                                        Link Expires In — <strong>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</strong>
                                        <span className="cfm-expiry-date ms-2">({expiryDate})</span>
                                    </label>
                                    <input
                                        type="range"
                                        className="form-range cfm-range"
                                        min={1}
                                        max={14}
                                        value={daysUntilExpiry}
                                        onChange={e => setDaysUntilExpiry(Number(e.target.value))}
                                    />
                                    <div className="cfm-range-labels">
                                        <span>1 day</span><span>14 days</span>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="cfm-field-label d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input cfm-check"
                                            checked={shareWithTeam}
                                            onChange={e => setShareWithTeam(e.target.checked)}
                                        />
                                        Share responses with all workspace members
                                    </label>
                                    <div className="cfm-field-hint">
                                        Responses are shared with all workspace members by default. Disable this to keep feedback private to just you and the workspace admin.
                                    </div>
                                </div>

                                {/* Summary */}
                                <div className="cfm-summary">
                                    <div className="cfm-summary-row">
                                        <span className="cfm-summary-label">Client</span>
                                        <span>{selectedClient?.client_name}</span>
                                    </div>
                                    <div className="cfm-summary-row">
                                        <span className="cfm-summary-label">Bills</span>
                                        <span>{selectedBillIds.length} selected</span>
                                    </div>
                                    <div className="cfm-summary-row">
                                        <span className="cfm-summary-label">Recipients</span>
                                        <span>{selectedContactIds.length} contact{selectedContactIds.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="cfm-summary-row">
                                        <span className="cfm-summary-label">Expires</span>
                                        <span>{expiryDate}</span>
                                    </div>
                                </div>

                                {sendError && (
                                    <div className="alert alert-danger py-2 mt-2 small">{sendError}</div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer cfm-footer">
                        {step > 0 && (
                            <button className="btn cfm-back-btn" onClick={() => setStep(s => s - 1)}>
                                Back
                            </button>
                        )}
                        <button className="btn cfm-cancel-btn" onClick={onClose}>Cancel</button>
                        {step < 3 ? (
                            <button
                                className="btn cfm-next-btn"
                                onClick={() => setStep(s => s + 1)}
                                disabled={!canAdvanceCurrent}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                className="btn cfm-send-btn"
                                onClick={handleSend}
                                disabled={!canSend}
                            >
                                {sending ? 'Sending…' : 'Send Request'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
